// C:\Users\asten\Desktop\arttustenius.com\src\components\Navbar\navbar.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiSun, FiMoon, FiMenu, FiX, FiArrowUpRight } from "react-icons/fi";
import { t as translate } from "../../i18n";
import Logo from "../../assets/logo.png";
import { useSiteContent } from "../../content/SiteContentContext";
// ------------------------------
// GA4 helper
// ------------------------------
const fireEvent = (name, params = {}) => {
  if (window.gtag) window.gtag("event", name, params);
};
/**
 * Navbar tukee kieliprefiksejä:
 * - fi: /
 * - en: /en/
 * - es: /es/
 *
 * App voi välittää (lang, prefix) propseina, mutta Navbar osaa myös päätellä ne pathname:stä.
 */
const Navbar = ({ lang: langProp, prefix: prefixProp } = {}) => {
  const siteContent = useSiteContent();
  const brandLogo = siteContent?.media?.brandLogo || Logo;
  const location = useLocation();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");
  const [showMenu, setShowMenu] = useState(false);
  const menuPanelRef = useRef(null);
  /**
   * Päättele kieli/prefix:
   * - jos App välittää propseina, käytetään niitä
   * - muuten päätellään suoraan URL:sta
   */
  const { lang, prefix, homePath, privacyPath, normalizedPathname } = useMemo(() => {
    const pathname = location?.pathname || "/";
    // Normalisoi: varmistetaan trailing slash vertailuja varten
    const normalizeTrailingSlash = (p) => (p.endsWith("/") ? p : `${p}/`);
    const normalizedPathname = normalizeTrailingSlash(pathname);
    // 1) propsit voittaa, jos annettu
    let lang = typeof langProp === "string" && langProp ? langProp : "";
    let prefix = typeof prefixProp === "string" ? prefixProp : "";
    // siivoa prefix: "/en/" -> "/en", "en" -> "/en", "" -> ""
    if (prefix) {
      if (!prefix.startsWith("/")) prefix = `/${prefix}`;
      prefix = prefix.replace(/\/+$/, ""); // poista trailing slashet
    }
    // 2) jos propsit puuttuu, päätellään URL:sta
    if (!prefix) {
      if (/^\/en(\/|$)/.test(pathname)) {
        lang = lang || "en";
        prefix = "/en";
      } else if (/^\/es(\/|$)/.test(pathname)) {
        lang = lang || "es";
        prefix = "/es";
      } else {
        lang = lang || "fi";
        prefix = "";
      }
    } else {
      // jos prefix annettu mutta lang puuttuu, täydennä
      if (!lang) {
        if (prefix === "/en") lang = "en";
        else if (prefix === "/es") lang = "es";
        else lang = "fi";
      }
    }
    // Home-polku aina muodossa "/" tai "/en/" tai "/es/"
    const homePath = prefix ? `${prefix}/` : "/";
    // Tietosuojapolku aina muodossa "/tietosuoja/" tai "/en/tietosuoja/"
    const privacyPath = prefix ? `${prefix}/tietosuoja/` : "/tietosuoja/";
    return { lang, prefix, homePath, privacyPath, normalizedPathname };
  }, [langProp, prefixProp, location.pathname]);
  // Yhteinen käännöshelperi lukee myös WordPressin dynaamiset tekstioverridet.
  const t = useCallback(
    (key, vars) => translate(key, lang, vars),
    [lang]
  );
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  const items = useMemo(
    () => {
      const pricePath = prefix
        ? `${prefix}/verkkosivujen-hinta`
        : "/verkkosivujen-hinta";
      return [
        { type: "section", to: "intro", label: t("nav.home") },
        { type: "section", to: "about", label: t("nav.about") },
        { type: "section", to: "skills", label: t("nav.skills") },
        { type: "section", to: "works", label: t("nav.references") },
        { type: "route", to: pricePath, label: t("nav.pricing") },
        { type: "section", to: "contact", label: t("nav.contact") },
      ];
    },
    [prefix, t]
  );
// Synkkaa html-luokka ja localStorage
  useEffect(() => {
    if (theme === "dark") document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
    localStorage.setItem("theme", theme);
    document.documentElement.style.transition =
      "background-color 0.25s ease, color 0.25s ease";
  }, [theme]);
  // Body scroll lock mobiilimenun auki ollessa
  useEffect(() => {
    if (!showMenu) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showMenu]);
  // ESC sulkee menun
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowMenu(false);
    };
    if (showMenu) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showMenu]);
  // Kun menu avautuu, fokus sisään (parempi a11y)
  useEffect(() => {
    if (!showMenu) return;
    const timer = setTimeout(() => {
      menuPanelRef.current?.querySelector("button, a")?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [showMenu]);
  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === "light" ? "dark" : "light";
      fireEvent("theme_toggle", {
        from: prev,
        to: next,
        page_path: location?.pathname || window.location?.pathname || "",
      });
      return next;
    });
  };
  const closeMobileMenu = useCallback(() => setShowMenu(false), []);
  // -------------------------------------------
  // Kielen vaihto: säilytä nykyinen polku ja vaihda prefiksi
  // - /tietosuoja/       -> /en/tietosuoja/
  // - /en/               -> /es/
  // - /es/tietosuoja/    -> /tietosuoja/
  // -------------------------------------------
  const stripLangPrefix = useCallback((p) => {
    const path = typeof p === "string" && p ? p : "/";
    const stripped = path.replace(/^\/(en|es)(?=\/|$)/, "");
    return stripped === "" ? "/" : stripped;
  }, []);
  const buildLangTarget = useCallback(
    (nextLang) => {
      const currentPath = location?.pathname || window.location?.pathname || "/";
      const base = stripLangPrefix(currentPath);
      const nextPrefix = nextLang === "en" ? "/en" : nextLang === "es" ? "/es" : "";
      return `${nextPrefix}${base}`.replace(/\/{2,}/g, "/");
    },
    [location?.pathname, stripLangPrefix]
  );
  const switchLang = useCallback(
    (nextLang) => {
      if (!nextLang || nextLang === lang) return;
      const target = buildLangTarget(nextLang);
      fireEvent("lang_switch", {
        from: lang,
        to: nextLang,
        from_prefix: prefix,
        to_path: target,
        page_path: location?.pathname || window.location?.pathname || "",
        is_mobile: window.innerWidth < 768,
      });
      closeMobileMenu();
      navigate(target, { replace: false });
    },
    [lang, prefix, buildLangTarget, navigate, location?.pathname, closeMobileMenu]
  );
  const scrollToSection = useCallback((section) => {
    const el = document.getElementById(section);
    if (!el) return;
    // Navbarin korkeus on h-16 (= 64px) + vähän ilmaa
    const NAV_OFFSET = 96;
    const y = el.getBoundingClientRect().top + window.pageYOffset - NAV_OFFSET;
    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  }, []);
  const handleNavigation = useCallback(
    (section) => {
      fireEvent("nav_click", {
        section,
        page_path: location?.pathname || window.location?.pathname || "",
        is_mobile: window.innerWidth < 768,
      });
      const isOnHome = normalizedPathname === homePath;
      if (!isOnHome) {
        navigate(homePath, { replace: false });
        setTimeout(() => scrollToSection(section), 300);
      } else {
        scrollToSection(section);
      }
      closeMobileMenu();
    },
    [location?.pathname, navigate, scrollToSection, homePath, normalizedPathname, closeMobileMenu]
  );
  const handleLogoClick = () => {
    fireEvent("nav_logo_click", { page_path: location?.pathname || window.location?.pathname || "" });
    handleNavigation("intro");
  };
  const openMenu = () => {
    setShowMenu(true);
    fireEvent("nav_mobile_open", { page_path: location?.pathname || window.location?.pathname || "" });
  };
  const closeMenu = () => {
    setShowMenu(false);
    fireEvent("nav_mobile_close", { page_path: location?.pathname || window.location?.pathname || "" });
  };
  return (
    <>
      {/* Sticky, premium navbar */}
      <nav
        role="navigation"
        aria-label={t("nav.ariaMain")}
        className={
          "fixed top-0 left-0 z-[999] w-full " +
          "backdrop-blur-md " +
          "bg-white/70 dark:bg-gray-950/55 " +
          "border-b border-gray-200/60 dark:border-white/10 " +
          "shadow-[0_8px_30px_rgba(0,0,0,0.08)] dark:shadow-[0_10px_40px_rgba(0,0,0,0.35)] " +
          "transition-colors duration-300"
        }
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="h-16 flex items-center justify-between flex-nowrap">
            {/* Logo */}
           <button
              onClick={handleLogoClick}
              className="group flex shrink-0 items-center gap-2 focus:outline-none"
              aria-label={t("nav.logoAria")}
              title={t("nav.logoTitle")}
            >
              <img
                src={brandLogo}
                alt=""
                aria-hidden="true"
                className="h-8 w-8 rounded-full object-contain transition-transform duration-200 group-hover:scale-105"
              />
              <span className="hidden font-extrabold tracking-tight text-sm text-gray-900 dark:text-white sm:inline">
                arttustenius.com
              </span>
            </button>
            {/* Desktop links */}
            <div className="hidden lg:flex items-center gap-6 my-4">
              {items.map((item) => (
                <button
                  key={item.to}
                  onClick={() => {
                    if (item.type === "route") {
                      navigate(item.to, { replace: false });
                      closeMobileMenu();
                      requestAnimationFrame(() => {
                        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
                      });
                      return;
                    }
                    handleNavigation(item.to);
                  }}
                  className={
                    // "Signature" hover: glow + glass + sweep + underline
                    "group relative isolate -mx-1 px-3 py-2 rounded-xl " +
                    "font-semibold text-[15px] " +
                    "text-gray-800 hover:text-gray-900 " +
                    "dark:text-gray-200 dark:hover:text-white " +
                    "transition-[color,transform] duration-300 ease-out " +
                    "hover:-translate-y-[1px] " +
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400/60"
                  }
                  title={item.label}
                  aria-label={item.label}
                >
                  <span
                    className={
                      "pointer-events-none absolute inset-0 -z-10 rounded-xl " +
                      "opacity-0 scale-90 blur-md " +
                      "bg-gradient-to-r from-lime-400/25 via-sky-400/20 to-indigo-400/25 " +
                      "transition-all duration-300 ease-out " +
                      "group-hover:opacity-100 group-hover:scale-100"
                    }
                  />
                 {/* Glass-kortti hoverissa */}
                  <span
                    className={
                      "pointer-events-none absolute inset-0 -z-10 rounded-xl " +
                      "border border-transparent " +
                      "bg-white/0 dark:bg-white/0 " +
                      "transition-all duration-300 ease-out " +
                      "group-hover:border-white/40 dark:group-hover:border-white/15 " +
                      "group-hover:bg-white/70 dark:group-hover:bg-white/5 " +
                      "group-hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] " +
                      "dark:group-hover:shadow-[0_12px_32px_rgba(0,0,0,0.25)]"
                    }
                  />
                  {/* Sheen / valokiila – tekee hoverista vähän “ei kaikilla” */}
                  <span
                    className={
                      "pointer-events-none absolute top-[-35%] left-[-28px] h-[170%] w-6 " +
                      "rotate-[18deg] rounded-full blur-[2px] " +
                      "bg-white/0 dark:bg-white/0 opacity-0 " +
                      "transition-all duration-500 ease-out " +
                      "group-hover:opacity-100 " +
                      "group-hover:left-[calc(100%+12px)] " +
                      "group-hover:bg-white/55 dark:group-hover:bg-white/20"
                    }
                  />
                  {/* Teksti */}
                  <span className="relative z-10 inline-block transition-transform duration-300 group-hover:-translate-y-[1px]">
                    {item.label}
                  </span>
                  {/* Alaviiva (sisäänrakennettu, ei perus hover:w-full -temppu) */}
                  <span className="pointer-events-none absolute left-2 right-2 -bottom-[2px] h-[2px] overflow-hidden rounded-full">
                    <span
                      className={
                        "absolute inset-0 origin-left scale-x-0 rounded-full " +
                        "bg-gradient-to-r from-lime-400 via-sky-400 to-indigo-400 " +
                        "transition-transform duration-300 ease-out " +
                        "group-hover:scale-x-100"
                      }
                    />
                  </span>
                </button>
              ))}
            </div>
            {/* Right side controls */}
            <div className="flex items-center gap-3">
              {/* Language switch (FI/EN/ES) */}
              <div
                className={
                  "hidden lg:inline-flex items-center overflow-hidden " +
                  "rounded-full border border-gray-200/60 dark:border-white/10 " +
                  "bg-white/60 dark:bg-white/10 shadow-sm"
                }
                role="group"
                aria-label={t("nav.language")}
                title={t("nav.language")}
              >
                {[
                  { code: "fi", label: "FI" },
                  { code: "en", label: "EN" },
                  { code: "es", label: "ES" },
                ].map((l) => {
                  const active = lang === l.code;
                  return (
                    <button
                      key={l.code}
                      onClick={() => switchLang(l.code)}
                      aria-pressed={active}
                      aria-label={t("nav.switchTo", { lang: l.label })}
                      title={t("nav.switchTo", { lang: l.label })}
                      className={
                        "px-3 h-10 text-xs font-extrabold tracking-wide " +
                        "transition-colors focus:outline-none focus:ring-2 focus:ring-lime-400/60 " +
                        (active
                          ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                          : "text-gray-800 hover:bg-white/85 dark:text-gray-200 dark:hover:bg-white/15")
                      }
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                aria-label={t("nav.themeToggle")}
                title={t("nav.themeToggle")}
                className={
                  "inline-flex items-center justify-center " +
                  "w-10 h-10 rounded-full " +
                  "bg-white/60 hover:bg-white/85 " +
                  "dark:bg-white/10 dark:hover:bg-white/15 " +
                  "border border-gray-200/60 dark:border-white/10 " +
                  "shadow-sm transition-all duration-200 " +
                  "focus:outline-none focus:ring-2 focus:ring-lime-400/60"
                }
              >
                {theme === "light" ? (
                  <FiMoon size={20} className="text-gray-400" />
                ) : (
                  <FiSun size={20} className="text-yellow-300" />
                )}
              </button>
              {/* Mobile menu button */}
              <button
                onClick={showMenu ? closeMenu : openMenu}
                className={
                  "lg:hidden inline-flex items-center justify-center " +
                  "w-10 h-10 rounded-full " +
                  "bg-white/60 hover:bg-white/85 " +
                  "dark:bg-white/10 dark:hover:bg-white/15 " +
                  "border border-gray-200/60 dark:border-white/10 " +
                  "shadow-sm transition-all duration-200 " +
                  "focus:outline-none focus:ring-2 focus:ring-lime-400/60"
                }
                aria-label={showMenu ? t("nav.closeMenu") : t("nav.openMenu")}
                title={showMenu ? t("nav.closeMenu") : t("nav.openMenu")}
              >
                {showMenu ? (
                  <FiX size={22} className="text-gray-800 dark:text-gray-100" />
                ) : (
                  <FiMenu size={22} className="text-gray-800 dark:text-gray-100" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>
      {/* Mobile overlay menu (premium) */}
      <div
        className={
          "lg:hidden fixed inset-0 z-[998] " +
          (showMenu ? "pointer-events-auto" : "pointer-events-none")
        }
        aria-hidden={!showMenu}
      >
        {/* Backdrop */}
        <div
          onClick={closeMenu}
          className={
            "absolute inset-0 transition-opacity duration-200 " +
            (showMenu ? "opacity-100" : "opacity-0") +
            " bg-black/45"
          }
        />
        {/* Panel */}
        <div
          ref={menuPanelRef}
          className={
            "absolute left-1/2 top-20 w-[92%] -translate-x-1/2 " +
            "rounded-2xl overflow-hidden " +
            "border border-gray-200/60 dark:border-white/10 " +
            "bg-white dark:bg-gray-950 " +
            "shadow-[0_18px_60px_rgba(0,0,0,0.35)] " +
            "backdrop-blur-md " +
            "transform transition-all duration-200 " +
            (showMenu ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3")
          }
          role="dialog"
          aria-label={t("nav.mobileMenu")}
        >
          {/* Header inside panel */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200/70 dark:border-white/10">
            <div className="flex items-center gap-2">
              <span className="font-extrabold tracking-tight text-gray-900 dark:text-white">
                {t("nav.menu")}
              </span>
              {/* Language switch (mobile) */}
              <div
                className={
                  "ml-2 inline-flex items-center overflow-hidden " +
                  "rounded-full border border-gray-200/60 dark:border-white/10 " +
                  "bg-gray-50 dark:bg-white/10"
                }
                role="group"
                aria-label={t("nav.language")}
              >
                {[
                  { code: "fi", label: "FI" },
                  { code: "en", label: "EN" },
                  { code: "es", label: "ES" },
                ].map((l) => {
                  const active = lang === l.code;
                  return (
                    <button
                      key={l.code}
                      onClick={() => switchLang(l.code)}
                      aria-pressed={active}
                      aria-label={t("nav.switchTo", { lang: l.label })}
                      className={
                        "px-3 h-9 text-xs font-extrabold tracking-wide " +
                        "transition-colors focus:outline-none focus:ring-2 focus:ring-lime-400/60 " +
                        (active
                          ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                          : "text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/15")
                      }
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              onClick={closeMenu}
              className={
                "inline-flex items-center justify-center w-10 h-10 rounded-full " +
                "bg-gray-100 hover:bg-gray-200 " +
                "dark:bg-white/10 dark:hover:bg-white/15 " +
                "transition-all focus:outline-none focus:ring-2 focus:ring-lime-400/60"
              }
              aria-label={t("nav.closeMenu")}
              title={t("nav.closeMenu")}
            >
              <FiX size={20} className="text-gray-800 dark:text-gray-100" />
            </button>
          </div>
          {/* Links */}
          <div className="px-3 py-3">
            {items.map((item) => (
              <button
                key={item.to}
                onClick={() => handleNavigation(item.to)}
                className={
                  "w-full flex items-center justify-between " +
                  "px-4 py-4 rounded-xl " +
                  "text-left font-semibold " +
                  "text-gray-900 dark:text-gray-100 " +
                  "hover:bg-gray-100 active:bg-gray-200 " +
                  "dark:hover:bg-white/10 dark:active:bg-white/15 " +
                  "transition-colors focus:outline-none focus:ring-2 focus:ring-lime-400/60"
                }
              >
                <span className="text-[15px]">{item.label}</span>
                <FiArrowUpRight className="opacity-70" />
              </button>
            ))}
            {/* Extra: tietosuoja */}
            <button
              onClick={() => {
                fireEvent("nav_click", {
                  section: "tietosuoja",
                  page_path: location?.pathname || window.location?.pathname || "",
                  is_mobile: true,
                });
                closeMobileMenu();
                navigate(privacyPath);
              }}
              className={
                "mt-2 w-full flex items-center justify-between " +
                "px-4 py-4 rounded-xl " +
                "text-left font-semibold " +
                "text-gray-900 dark:text-gray-100 " +
                "hover:bg-gray-100 active:bg-gray-200 " +
                "dark:hover:bg-white/10 dark:active:bg-white/15 " +
                "transition-colors focus:outline-none focus:ring-2 focus:ring-lime-400/60"
              }
            >
              <span className="text-[15px]">{t("nav.privacy")}</span>
              <FiArrowUpRight className="opacity-70" />
            </button>
          </div>
          {/* Footer note */}
          <div className="px-5 pb-5 pt-2 text-xs text-gray-600 dark:text-gray-400">
            {t("nav.hintEsc")} <span className="font-semibold">Esc</span>.
          </div>
        </div>
      </div>
    </>
  );
};
export default Navbar;
